(() => {
  angular.module('finance').component('financeSettingsNetsuiteHeldSubmissions', {
    templateUrl: require('./netSuiteHeldSubmissions.component.html'),
    controller: NetSuiteHeldSubmissionsController,
    controllerAs: 'vm'
  });

  NetSuiteHeldSubmissionsController.$inject = [
    '$scope',
    'abp.services.finance.netSuite',
    'financeConstants'
  ];

  // Mirrors NetSuiteSubmissionRecordType on the finance service.

  const recordTypes = {
    saleTransaction: 0,
    paymentAccountingPosting: 1
  };

  function NetSuiteHeldSubmissionsController($scope, netSuiteSvc, financeConstants) {
    const vm = this;

    vm.loading = 0;
    vm.recordTypes = recordTypes;
    vm.selectedIds = [];

    // Held counts per record type, shown on the switch so the other list's size is known
    // without opening it.

    vm.heldCounts = {};

    vm.requestParams = {
      recordType: recordTypes.saleTransaction,
      skipCount: 0,
      maxResultCount: financeConstants.grid.defaultPageSize
    };

    vm.gridOptions = {
      appScopeProvider: vm,
      paginationPageSizes: financeConstants.grid.defaultPageSizes,
      paginationPageSize: financeConstants.grid.defaultPageSize,
      paginationCurrentPage: 1,
      useExternalPagination: true,
      enableSorting: false,
      multiSelect: true,
      columnDefs: [
        {
          displayName: 'Reference number',
          field: 'referenceNumber',
          minWidth: 200
        },
        {
          displayName: 'Failures',
          field: 'failureCount',
          width: 90
        },
        {
          displayName: 'Last error',
          field: 'error',
          minWidth: 300,
          cellTooltip: true
        },
        {
          displayName: 'Last failed',
          field: 'failureTime',
          cellFilter: "momentFormat: 'L LTS'",
          width: 180
        }
      ],
      onRegisterApi(gridApi) {
        $scope.gridApi = gridApi;
        gridApi.pagination.on.paginationChanged(
          $scope,
          App.setPagination(vm.requestParams, getHeldSubmissions)
        );
        gridApi.selection.on.rowSelectionChanged($scope, updateSelectedIds);
        gridApi.selection.on.rowSelectionChangedBatch($scope, updateSelectedIds);
      },
      data: []
    };

    vm.selectRecordType = selectRecordType;
    vm.refresh = refresh;
    vm.releaseSelected = releaseSelected;
    vm.releaseAll = releaseAll;

    init();

    function init() {
      getHeldSubmissions();
      getHeldCounts();
    }

    function selectRecordType(recordType) {
      if (vm.requestParams.recordType === recordType) return;

      vm.requestParams.recordType = recordType;
      reloadFromFirstPage();
    }

    function refresh() {
      reloadFromFirstPage();
      getHeldCounts();
    }

    // Back to the first page, as the current one may no longer exist. Changing the page makes the
    // grid fetch it through App.setPagination, so only fetch here when already on the first page.

    function reloadFromFirstPage() {
      vm.requestParams.skipCount = 0;

      // The rows on screen may be of the other kind or already released, so nothing stays
      // selectable while the reload is pending.

      clearSelection();

      if (vm.gridOptions.paginationCurrentPage !== 1) {
        vm.gridOptions.paginationCurrentPage = 1;
      } else {
        getHeldSubmissions();
      }
    }

    function getHeldSubmissions() {
      const params = angular.copy(vm.requestParams);

      vm.loading += 1;

      netSuiteSvc
        .getHeldSubmissions(params)
        .success((data) => {
          vm.heldCounts[params.recordType] = data.totalCount;

          // A slower response for a kind or page no longer shown is dropped.

          const isCurrent =
            params.recordType === vm.requestParams.recordType &&
            params.skipCount === vm.requestParams.skipCount;

          if (!isCurrent) return;

          clearSelection();
          vm.gridOptions.totalItems = data.totalCount;
          vm.gridOptions.data = data.items;
          vm.loadedRecordType = params.recordType;
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    // Also resets the grid's select-all box, which ui-grid leaves ticked when new data is empty.

    function clearSelection() {
      if ($scope.gridApi) $scope.gridApi.selection.clearSelectedRows();
      vm.selectedIds = [];
    }

    function getHeldCounts() {
      _.forEach(_.values(recordTypes), (recordType) => {
        netSuiteSvc
          .getHeldSubmissions({ recordType, skipCount: 0, maxResultCount: 1 })
          .success((data) => {
            vm.heldCounts[recordType] = data.totalCount;
          });
      });
    }

    function updateSelectedIds() {
      vm.selectedIds = _.map($scope.gridApi.selection.getSelectedRows(), 'id');
    }

    function releaseSelected() {
      // Read when clicked, as a page change can clear the selection while the prompt is open. The
      // kind is the one the rows were loaded as, which lags the switch until its list arrives.

      const ids = vm.selectedIds.slice();
      const recordType = vm.loadedRecordType;

      if (!ids.length || recordType !== vm.requestParams.recordType) {
        abp.notify.warn('Select at least one record to release.');
        return;
      }

      confirmRelease(ids.length, recordType, () =>
        netSuiteSvc.releaseHeldSubmissions({ recordType, ids }).success((releasedCount) => {
          const skippedCount = ids.length - releasedCount;
          const skipped = skippedCount > 0 ? ` ${skippedCount} had already been released or sent.` : '';

          abp.notify.success(`Released ${releasedCount} ${describeRecords(releasedCount, recordType)}.${skipped}`);
        })
      );
    }

    function releaseAll() {
      const { recordType } = vm.requestParams;
      const count = vm.heldCounts[recordType];

      if (!count) {
        abp.notify.info('Nothing is held.');
        return;
      }

      confirmRelease(count, recordType, () =>
        netSuiteSvc.releaseAllHeldSubmissions({ recordType }).success((releasingCount) => {
          if (!releasingCount) {
            abp.notify.info('Nothing is held.');
            return;
          }

          abp.notify.success(
            `Releasing ${releasingCount} ${describeRecords(releasingCount, recordType)} in the background. ` +
              'Refresh in a minute to see the result.'
          );
        })
      );
    }

    function confirmRelease(count, recordType, release) {
      abp.message.confirm(
        `Release ${count} held ${describeRecords(count, recordType)}? Records created before the ` +
          'NetSuite go-live date are sent within a few minutes; the rest are sent with the next nightly run.',
        App.localize('AreYouSure'),
        (isConfirmed) => {
          if (!isConfirmed) return;

          vm.loading += 1;
          release().finally(() => {
            vm.loading -= 1;
            refresh();
          });
        }
      );
    }

    function describeRecords(count, recordType) {
      if (recordType === recordTypes.paymentAccountingPosting)
        return count === 1 ? 'payment posting' : 'payment postings';

      return count === 1 ? 'invoice or credit memo' : 'invoices and credit memos';
    }
  }
})();
